import { css, StyleSheet } from 'aphrodite';
import { nanoid } from 'nanoid';
import {
	ChangeEventHandler,
	Children,
	ComponentProps,
	createContext,
	FormEvent,
	FormEventHandler,
	forwardRef,
	HTMLAttributes,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { LoaderCircleIcon } from '../../../components/svgs/icons/LoaderCircleIcon';
import { MicIcon } from '../../../components/svgs/icons/MicIcon';
import { PaperPlaneIcon } from '../../../components/svgs/icons/PaperPlaneIcon';
import { SquareIcon } from '../../../components/svgs/icons/SquareIcon';
import { XIcon } from '../../../components/svgs/icons/XIcon';
import { pulse, spin } from '../../../styles/animations';
import { antiFlashWhite } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { InputGroupV2 as InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from '../../InputGroup';
import { PaperClipIcon } from '@WebComponents/svgs/icons/PaperClipIcon';

export type FileUIPart = {
	type: 'file';
	/**
	 * IANA media type of the file.
	 */
	mediaType: string;
	/**
	 * Optional filename of the file.
	 */
	filename?: string;
	/**
	 * The URL of the file.
	 * It can either be a URL to a hosted file or a Data URL.
	 */
	url: string;
	/**
	 * The original File object (only present for files added via local add path).
	 * Consumers performing uploads can read this to access the raw bytes.
	 */
	file?: File;
};

export type PromptInputMessage = {
	text: string;
	files: FileUIPart[];
};

export type AttachmentsContext = {
	files: (FileUIPart & { id: string })[];
	add: (files: File[] | FileList) => void;
	remove: (id: string) => void;
	clear: () => void;
	openFileDialog: () => void;
	fileInputRef: RefObject<HTMLInputElement | null>;
};

export type TextInputContext = {
	value: string;
	setInput: (v: string) => void;
	clear: () => void;
};

export type PromptInputControllerProps = {
	textInput: TextInputContext;
	attachments: AttachmentsContext;
	/** INTERNAL: Allows PromptInput to register its file textInput + "open" callback */
	__registerFileInput: (ref: RefObject<HTMLInputElement | null>, open: () => void) => void;
};

const PromptInputController = createContext<PromptInputControllerProps | null>(null);

const ProviderAttachmentsContext = createContext<AttachmentsContext | null>(null);

export type PromptInputProps = Omit<AphroditeStyled<HTMLAttributes<HTMLFormElement>>, 'onSubmit' | 'onError'> & {
	accept?: string; // e.g., "image/*" or leave undefined for any
	multiple?: boolean;
	// When true, accepts drops anywhere on document. Default false (opt-in).
	globalDrop?: boolean;
	// Render a hidden input with given name and keep it in sync for native form posts. Default false.
	syncHiddenInput?: boolean;
	// Minimal constraints
	maxFiles?: number;
	maxFileSize?: number; // bytes
	onError?: (err: { code: 'max_files' | 'max_file_size' | 'accept'; message: string }) => void;
	onSubmit: (message: PromptInputMessage, event: FormEvent<HTMLFormElement>) => void | Promise<void>;
};

// Optional variants (do NOT throw). Useful for dual-mode components.
const useOptionalPromptInputController = () => useContext(PromptInputController);

const LocalAttachmentsContext = createContext<AttachmentsContext | null>(null);

const useOptionalProviderAttachments = () => useContext(ProviderAttachmentsContext);

export const usePromptInputAttachments = () => {
	// Dual-mode: prefer provider if present, otherwise use local
	const provider = useOptionalProviderAttachments();
	const local = useContext(LocalAttachmentsContext);
	const context = provider ?? local;
	if (!context) {
		throw new Error('usePromptInputAttachments must be used within a PromptInput or PromptInputProvider');
	}
	return context;
};

export const PromptInput = ({
	styles = [],
	accept,
	multiple,
	globalDrop,
	syncHiddenInput,
	maxFiles,
	maxFileSize,
	onError,
	onSubmit,
	children,
	...props
}: PromptInputProps) => {
	// Try to use a provider controller if present
	const controller = useOptionalPromptInputController();
	const usingProvider = !!controller;

	// Refs
	const inputRef = useRef<HTMLInputElement | null>(null);
	const formRef = useRef<HTMLFormElement | null>(null);

	// ----- Local attachments (only used when no provider)
	const [items, setItems] = useState<(FileUIPart & { id: string })[]>([]);
	const files = usingProvider ? controller.attachments.files : items;

	// Keep a ref to files for cleanup on unmount (avoids stale closure)
	const filesRef = useRef(files);
	filesRef.current = files;

	const openFileDialogLocal = useCallback(() => {
		inputRef.current?.click();
	}, []);

	const matchesAccept = useCallback(
		(f: File) => {
			if (!accept || accept.trim() === '') {
				return true;
			}
			if (accept.includes('image/*')) {
				return f.type.startsWith('image/');
			}
			// NOTE: keep simple; expand as needed
			return true;
		},
		[accept]
	);

	const addLocal = useCallback(
		(fileList: File[] | FileList) => {
			const incoming = Array.from(fileList);
			const accepted = incoming.filter(f => matchesAccept(f));
			if (incoming.length && accepted.length === 0) {
				onError?.({
					code: 'accept',
					message: 'No files match the accepted types.',
				});
				return;
			}
			const withinSize = (f: File) => (maxFileSize ? f.size <= maxFileSize : true);
			const sized = accepted.filter(withinSize);
			if (accepted.length > 0 && sized.length === 0) {
				onError?.({
					code: 'max_file_size',
					message: 'All files exceed the maximum size.',
				});
				return;
			}

			setItems(prev => {
				const capacity = typeof maxFiles === 'number' ? Math.max(0, maxFiles - prev.length) : undefined;
				const capped = typeof capacity === 'number' ? sized.slice(0, capacity) : sized;
				if (typeof capacity === 'number' && sized.length > capacity) {
					onError?.({
						code: 'max_files',
						message: 'Too many files. Some were not added.',
					});
				}
				const next: (FileUIPart & { id: string })[] = [];
				for (const file of capped) {
					next.push({
						id: nanoid(),
						type: 'file',
						url: URL.createObjectURL(file),
						mediaType: file.type,
						filename: file.name,
						file,
					});
				}
				return prev.concat(next);
			});
		},
		[matchesAccept, maxFiles, maxFileSize, onError]
	);

	const removeLocal = useCallback(
		(id: string) =>
			setItems(prev => {
				const found = prev.find(file => file.id === id);
				if (found?.url) {
					URL.revokeObjectURL(found.url);
				}
				return prev.filter(file => file.id !== id);
			}),
		[]
	);

	const clearLocal = useCallback(
		() =>
			setItems(prev => {
				for (const file of prev) {
					if (file.url) {
						URL.revokeObjectURL(file.url);
					}
				}
				return [];
			}),
		[]
	);

	const add = usingProvider ? controller.attachments.add : addLocal;
	const remove = usingProvider ? controller.attachments.remove : removeLocal;
	const clear = usingProvider ? controller.attachments.clear : clearLocal;
	const openFileDialog = usingProvider ? controller.attachments.openFileDialog : openFileDialogLocal;

	// Let provider know about our hidden file input so external menus can call openFileDialog()
	useEffect(() => {
		if (!usingProvider) return;
		controller.__registerFileInput(inputRef, () => inputRef.current?.click());
	}, [usingProvider, controller]);

	// Note: File input cannot be programmatically set for security reasons
	// The syncHiddenInput prop is no longer functional
	useEffect(() => {
		if (syncHiddenInput && inputRef.current && files.length === 0) {
			inputRef.current.value = '';
		}
	}, [files, syncHiddenInput]);

	// Attach drop handlers on nearest form and document (opt-in)
	useEffect(() => {
		const form = formRef.current;
		if (!form) return;

		const onDragOver = (e: DragEvent) => {
			if (e.dataTransfer?.types?.includes('Files')) {
				e.preventDefault();
			}
		};
		const onDrop = (e: DragEvent) => {
			if (e.dataTransfer?.types?.includes('Files')) {
				e.preventDefault();
			}
			if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
				add(e.dataTransfer.files);
			}
		};
		form.addEventListener('dragover', onDragOver);
		form.addEventListener('drop', onDrop);
		return () => {
			form.removeEventListener('dragover', onDragOver);
			form.removeEventListener('drop', onDrop);
		};
	}, [add]);

	useEffect(() => {
		if (!globalDrop) return;

		const onDragOver = (e: DragEvent) => {
			if (e.dataTransfer?.types?.includes('Files')) {
				e.preventDefault();
			}
		};
		const onDrop = (e: DragEvent) => {
			if (e.dataTransfer?.types?.includes('Files')) {
				e.preventDefault();
			}
			if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
				add(e.dataTransfer.files);
			}
		};
		document.addEventListener('dragover', onDragOver);
		document.addEventListener('drop', onDrop);
		return () => {
			document.removeEventListener('dragover', onDragOver);
			document.removeEventListener('drop', onDrop);
		};
	}, [add, globalDrop]);

	useEffect(
		() => () => {
			if (!usingProvider) {
				for (const f of filesRef.current) {
					if (f.url) URL.revokeObjectURL(f.url);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- cleanup only on unmount; filesRef always current
		[usingProvider]
	);

	const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
		if (event.currentTarget.files) {
			add(event.currentTarget.files);
		}
		// Reset input value to allow selecting files that were previously removed
		event.currentTarget.value = '';
	};

	const convertBlobUrlToDataUrl = async (url: string): Promise<string | null> => {
		try {
			const response = await fetch(url);
			const blob = await response.blob();
			return new Promise(resolve => {
				const reader = new FileReader();
				reader.onloadend = () => resolve(reader.result as string);
				reader.onerror = () => resolve(null);
				reader.readAsDataURL(blob);
			});
		} catch {
			return null;
		}
	};

	const ctx = useMemo<AttachmentsContext>(
		() => ({
			files: files.map(item => ({ ...item, id: item.id })),
			add,
			remove,
			clear,
			openFileDialog,
			fileInputRef: inputRef,
		}),
		[files, add, remove, clear, openFileDialog]
	);

	const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
		event.preventDefault();

		const form = event.currentTarget;
		const text = usingProvider
			? controller.textInput.value
			: (() => {
					const formData = new FormData(form);
					return (formData.get('message') as string) || '';
				})();

		// Reset form immediately after capturing text to avoid race condition
		// where user input during async blob conversion would be lost
		if (!usingProvider) {
			form.reset();
		}

		// Convert blob URLs to data URLs asynchronously
		Promise.all(
			files.map(async ({ id, ...item }) => {
				if (item.url && item.url.startsWith('blob:')) {
					const dataUrl = await convertBlobUrlToDataUrl(item.url);
					// If conversion failed, keep the original blob URL
					return {
						...item,
						url: dataUrl ?? item.url,
					};
				}
				return item;
			})
		)
			.then((convertedFiles: FileUIPart[]) => {
				try {
					const result = onSubmit({ text, files: convertedFiles }, event);

					// Handle both sync and async onSubmit
					if (result instanceof Promise) {
						result
							.then(() => {
								clear();
								if (usingProvider) {
									controller.textInput.clear();
								}
							})
							.catch(() => {
								// Don't clear on error - user may want to retry
							});
					} else {
						// Sync function completed without throwing, clear attachments
						clear();
						if (usingProvider) {
							controller.textInput.clear();
						}
					}
				} catch {
					// Don't clear on error - user may want to retry
				}
			})
			.catch(() => {
				// Don't clear on error - user may want to retry
			});
	};

	// Render with or without local provider
	const inner = (
		<>
			<input
				accept={accept}
				aria-label='Upload files'
				className={css(bs.hidden)}
				multiple={multiple}
				onChange={handleChange}
				ref={inputRef}
				title='Upload files'
				type='file'
			/>
			<form className={css(s.form, ...styles)} onSubmit={handleSubmit} ref={formRef} {...props}>
				<InputGroup styles={[s.inputGroup]}>{children}</InputGroup>
			</form>
		</>
	);

	return usingProvider ? (
		inner
	) : (
		<LocalAttachmentsContext.Provider value={ctx}>{inner}</LocalAttachmentsContext.Provider>
	);
};

export type PromptInputBodyProps = AphroditeStyled<HTMLAttributes<HTMLDivElement>>;

export const PromptInputBody = ({ styles = [], ...props }: PromptInputBodyProps) => (
	<div className={css(s.body, ...styles)} {...props} />
);

export type PromptInputTextareaProps = React.ComponentProps<typeof InputGroupTextarea>;

export const PromptInputTextarea = forwardRef<HTMLTextAreaElement, PromptInputTextareaProps>(
	({ onChange, styles = [], placeholder = 'What would you like to know?', ...props }, ref) => {
		const controller = useOptionalPromptInputController();
		const attachments = usePromptInputAttachments();
		const [isComposing, setIsComposing] = useState(false);

		const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
			if (e.key === 'Enter') {
				if (isComposing || e.nativeEvent.isComposing) {
					return;
				}
				if (e.shiftKey) {
					return;
				}
				e.preventDefault();

				// Check if the submit button is disabled before submitting
				const form = e.currentTarget.form;
				const submitButton = form?.querySelector('button[type="submit"]') as HTMLButtonElement | null;
				if (submitButton?.disabled) {
					return;
				}

				form?.requestSubmit();
			}

			// Remove last attachment when Backspace is pressed and textarea is empty
			if (e.key === 'Backspace' && e.currentTarget.value === '' && attachments.files.length > 0) {
				e.preventDefault();
				const lastAttachment = attachments.files.at(-1);
				if (lastAttachment) {
					attachments.remove(lastAttachment.id);
				}
			}
		};

		const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = event => {
			const items = event.clipboardData?.items;

			if (!items) {
				return;
			}

			const files: File[] = [];

			for (const item of items) {
				if (item.kind === 'file') {
					const file = item.getAsFile();
					if (file) {
						files.push(file);
					}
				}
			}

			if (files.length > 0) {
				event.preventDefault();
				attachments.add(files);
			}
		};

		const controlledProps = controller
			? {
					value: controller.textInput.value,
					onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
						controller.textInput.setInput(e.currentTarget.value);
						onChange?.(e);
					},
				}
			: {
					onChange,
				};

		return (
			<InputGroupTextarea
				styles={[s.inputGroupTextArea, ...styles]}
				name='message'
				onCompositionEnd={() => setIsComposing(false)}
				onCompositionStart={() => setIsComposing(true)}
				onKeyDown={handleKeyDown}
				onPaste={handlePaste}
				placeholder={placeholder}
				{...props}
				{...controlledProps}
				ref={ref}
			/>
		);
	}
);
PromptInputTextarea.displayName = 'PromptInputTextarea';

export type PromptInputFooterProps = Omit<React.ComponentProps<typeof InputGroupAddon>, 'align'>;

export const PromptInputFooter = ({ styles = [], ...props }: PromptInputFooterProps) => (
	<InputGroupAddon align='blockEnd' styles={[s.promptInputFooter, ...styles]} {...props} />
);

export type PromptInputToolsProps = AphroditeStyled<HTMLAttributes<HTMLDivElement>>;

export const PromptInputTools = ({ styles = [], ...props }: PromptInputToolsProps) => (
	<div className={css(s.promptInputTools, ...styles)} {...props} />
);

export type PromptInputButtonProps = React.ComponentProps<typeof InputGroupButton>;

export const PromptInputButton = ({ variant = 'ghost', styles, size, ...props }: PromptInputButtonProps) => {
	const newSize = size ?? (Children.count(props.children) > 1 ? 'sm' : 'iconSm');

	return <InputGroupButton styles={styles} size={newSize} type='button' variant={variant} {...props} />;
};

export const PromptInputAttachmentButton = (props: PromptInputButtonProps) => {
	const attachments = usePromptInputAttachments();
	return (
		<PromptInputButton aria-label='Attach file' onClick={attachments.openFileDialog} {...props}>
			<PaperClipIcon />
		</PromptInputButton>
	);
};

export type PromptInputSubmitProps = React.ComponentProps<typeof InputGroupButton> & {
	status?: 'loading' | 'streaming' | 'error' | 'idle';
};

export const PromptInputSubmit = ({
	variant = 'primary',
	size = 'iconSm',
	status,
	children,
	...props
}: PromptInputSubmitProps) => {
	let Icon = <PaperPlaneIcon width={16} height={16} />;

	if (status === 'loading') {
		Icon = <LoaderCircleIcon className={css(s.loaderCircle)} />;
	} else if (status === 'streaming') {
		Icon = <SquareIcon width={16} height={16} />;
	} else if (status === 'error') {
		Icon = <XIcon width={16} height={16} />;
	}

	return (
		<InputGroupButton aria-label='Submit' size={size} type='submit' variant={variant} {...props}>
			{children ?? Icon}
		</InputGroupButton>
	);
};

interface SpeechRecognition extends EventTarget {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	start(): void;
	stop(): void;
	onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
	onend: ((this: SpeechRecognition, ev: Event) => any) | null;
	onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
	onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
}

interface SpeechRecognitionEvent extends Event {
	results: SpeechRecognitionResultList;
	resultIndex: number;
}

type SpeechRecognitionResultList = {
	readonly length: number;
	item(index: number): SpeechRecognitionResult;
	[index: number]: SpeechRecognitionResult;
};

type SpeechRecognitionResult = {
	readonly length: number;
	item(index: number): SpeechRecognitionAlternative;
	[index: number]: SpeechRecognitionAlternative;
	isFinal: boolean;
};

type SpeechRecognitionAlternative = {
	transcript: string;
	confidence: number;
};

interface SpeechRecognitionErrorEvent extends Event {
	error: string;
}

declare global {
	interface Window {
		SpeechRecognition: new () => SpeechRecognition;
		webkitSpeechRecognition: new () => SpeechRecognition;
	}
}

export type PromptInputSpeechButtonProps = React.ComponentProps<typeof PromptInputButton> & {
	textareaRef?: RefObject<HTMLTextAreaElement | null>;
	onTranscriptionChange?: (text: string) => void;
};

export const PromptInputSpeechButton = ({
	styles = [],
	textareaRef,
	onTranscriptionChange,
	...props
}: PromptInputSpeechButtonProps) => {
	const [isListening, setIsListening] = useState(false);
	const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
	const recognitionRef = useRef<SpeechRecognition | null>(null);

	useEffect(() => {
		if (typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)) {
			const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
			const speechRecognition = new SpeechRecognition();

			speechRecognition.continuous = true;
			speechRecognition.interimResults = true;
			speechRecognition.lang = 'en-US';

			speechRecognition.onstart = () => {
				setIsListening(true);
			};

			speechRecognition.onend = () => {
				setIsListening(false);
			};

			speechRecognition.onresult = event => {
				let finalTranscript = '';

				for (let i = event.resultIndex; i < event.results.length; i++) {
					const result = event.results[i];
					if (result.isFinal) {
						finalTranscript += result[0]?.transcript ?? '';
					}
				}

				if (finalTranscript && textareaRef?.current) {
					const textarea = textareaRef.current;
					const currentValue = textarea.value;
					const newValue = currentValue + (currentValue ? ' ' : '') + finalTranscript;

					textarea.value = newValue;
					textarea.dispatchEvent(new Event('input', { bubbles: true }));
					onTranscriptionChange?.(newValue);
				}
			};

			speechRecognition.onerror = event => {
				console.error('Speech recognition error:', event.error);
				setIsListening(false);
			};

			recognitionRef.current = speechRecognition;
			setRecognition(speechRecognition);
		}

		return () => {
			if (recognitionRef.current) {
				recognitionRef.current.stop();
			}
		};
	}, [textareaRef, onTranscriptionChange]);

	const toggleListening = useCallback(() => {
		if (!recognition) {
			return;
		}

		if (isListening) {
			recognition.stop();
		} else {
			recognition.start();
		}
	}, [recognition, isListening]);

	return (
		<PromptInputButton
			styles={[s.promptInputSpeechButton, ...(isListening ? [s.promptInputButtonListening] : []), ...styles]}
			disabled={!recognition}
			onClick={toggleListening}
			{...props}
		>
			<MicIcon width={16} height={16} />
		</PromptInputButton>
	);
};

export type PromptInputHeaderProps = Omit<ComponentProps<typeof InputGroupAddon>, 'align'>;

export const PromptInputHeader = ({ styles = [], ...props }: PromptInputHeaderProps) => (
	<InputGroupAddon align='blockEnd' styles={[s.promptInputHeader, ...styles]} {...props} />
);

const s = StyleSheet.create({
	body: {
		display: 'contents',
	},
	form: {
		width: '100%',
	},
	inputGroup: {
		overflow: 'hidden',
	},
	inputGroupTextArea: {
		fieldSizing: 'content',
		maxHeight: 192,
		minHeight: 0,
	},
	promptInputFooter: {
		justifyContent: 'space-between',
		gap: 4,
	},
	loaderCircle: {
		width: 16,
		height: 16,
		animationName: spin,
		animationTimingFunction: 'linear',
		animationDuration: '1s',
		animationIterationCount: 'infinite',
	},
	promptInputSpeechButton: {
		position: 'relative',
		transitionProperty: 'all',
		transitionDuration: '0.2s',
		':has(*) > svg': {
			width: 16,
			height: 16,
		},
	},
	promptInputButtonListening: {
		background: antiFlashWhite,
		animationName: pulse,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.6, 1)',
	},
	promptInputTools: {
		display: 'flex',
		alignItems: 'center',
		gap: 4,
	},
	promptInputHeader: {
		order: -9999,
		flexWrap: 'wrap',
		gap: 4,
	},
});
